// import React, { ChangeEvent } from 'react';
// import './ChatInputField.css';

// interface Props {
//   value: string;
//   label: string;
//   onValueChange: (event: ChangeEvent<HTMLInputElement>) => void;
//   onEnterPress?: () => void;
// }

// function ChatInputField({ value, label, onValueChange, onEnterPress }: Props) {
//   const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
//     if (event.key === 'Enter' && !event.shiftKey) {
//       onEnterPress?.(); // Trigger only when Enter is pressed without Shift
//       event.preventDefault(); // Prevent any default behavior like form submission
//     }
//   };
//   return (
//     <input
//       type="text"
//       value={value}
//       placeholder={label}
//       onChange={onValueChange}
//       onKeyDown={onEnterPress && handleKeyDown}
//       className="ChatInputField"
//     ></input>
//   );
// }

// export default ChatInputField;

import React, { ChangeEvent, KeyboardEvent, useEffect, useRef } from 'react';
import './ChatInputField.css';

interface Props {
  value: string;
  label: string; // placeholder text
  /** Called whenever text changes. */
  onValueChange: (value: string) => void; // modernized signature (string)
  /** Called when user presses Enter (w/out Shift). */
  onEnterPress?: () => void;
  /** Max lines to auto-grow before scrolling. Default: 4. */
  maxLines?: number;
  /** Extra classNames if needed. */
  className?: string;
}

/**
 * Auto-expanding multiline chat input.
 * - Grows up to `maxLines` lines, then scrolls.
 * - Enter sends; Shift+Enter inserts newline.
 */
function ChatInputField({
  value,
  label,
  onValueChange,
  onEnterPress,
  maxLines = 4,
  className = '',
}: Props): JSX.Element {
  const ref = useRef<HTMLTextAreaElement>(null);

  const resize = () => {
    const el = ref.current;
    if (!el) return;

    // allow shrinking
    el.style.height = 'auto';

    const cs = window.getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight || '20');
    const padTop = parseFloat(cs.paddingTop || '0');
    const padBottom = parseFloat(cs.paddingBottom || '0');
    const maxHeight = lineHeight * maxLines + padTop + padBottom;

    const newHeight = Math.min(el.scrollHeight, maxHeight);
    el.style.height = `${newHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  };

  // resize on mount + whenever value changes externally
  useEffect(resize, [value, maxLines]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onValueChange(e.target.value);
    // resize *after* React state update paint; rAF ensures DOM updated
    requestAnimationFrame(resize);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onEnterPress?.();
    }
  };

  return (
    <textarea
      ref={ref}
      value={value}
      placeholder={label}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      rows={1}
      className={`ChatInputField ${className}`}
    />
  );
}

export default ChatInputField;
